import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { ticketTypeAvailable, salesStartInFuture } from './WebOrderForm';
import CalendarEventInterestForm from './CalendarEventInterestForm';
import { trackXPixelEvent, X_PIXEL_ADD_TO_CART } from '../utils/xPixel';
import { upcomingPriceText } from '../utils/upcomingPrice';
import { groupPriceOptions, rowLabel, quantityOptionsWithinOrderLimit } from '../utils/priceOptions';

const ticketTypeDisabled = (ticketType) => {
  return (
    ticketType.sales_ended || ticketType.sold_out || ticketType.sales_paused
  );
}

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const listEveryTicketType = () => true;

const ChooseTicketTypes = ({
  team,
  csrfToken,
  confirm,
  adminPreview,
  ticketTypes,
  promoCode,
  updateTicketTypeQuantity,
  buildTicketReservation,
  visibleTicketTypes,
  addOns,
  ticketReservation,
  postToParent,
  formatItemsForGoogleTagManager,
  userContext,
  announceListEnabled,
  waitlistEnabled,
  ticketGroups,
  findTicketGroup,
  ownsUnavailableState = true,
  previewFilter = listEveryTicketType
}) => {

  const [showPresalePrompt, setShowPresalePrompt] = useState(false);
  const [availibleTickets, setAvailibleTickets] = useState([]);
  const [visibleTickets, setVisibleTickets] = useState([]);
  const [scheduledTickets, setScheduledTickets] = useState([]);
  const anyVisibleTicketTypePaused = visibleTickets.some((ticketType) => ticketType.sales_paused);

  useEffect(() => {
    setVisibleTickets(
      adminPreview ? ticketTypes.filter(previewFilter) : visibleTicketTypes(confirm, ticketTypes, promoCode)
    );
  }, [ticketTypes, promoCode, visibleTicketTypes, adminPreview, previewFilter]);

  useEffect(() => {
    setAvailibleTickets(
      adminPreview
        ? visibleTickets
        : visibleTickets.filter((ticketType) => ticketTypeAvailable(ticketType))
    );

    setScheduledTickets(
      visibleTickets
        .filter((ticketType) => ticketType.scheduled)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    );
  }, [visibleTickets, adminPreview]);

  return (
    <div>
      {visibleTickets.length > 0 && groupPriceOptions(visibleTickets).map(({ heading, rows }, index) =>
        <div className="card border-0"
             style={{"background": "#f9f9f9", "marginBottom": "15px"}}
             key={index}>
          <div className="card-body"
               style={{
                 "paddingLeft": "15px",
                 "paddingRight": "15px"
               }}>
            {heading ? (
              <p className="mb-2" style={{"fontSize": "16px"}} data-testid="price-option-heading">
                <strong>{heading}</strong>
              </p>
            ) : null}
            {rows.map((ticketType, rowIndex) =>
            <div className={rowIndex > 0 ? "row mt-3" : "row"}
                 key={ticketType.id}
                 style={ticketTypeDisabled(ticketType) ? {"color": "#686868"} : {}}>
              <div className="col">
                <p className="mb-0"
                   style={{"fontSize": "16px"}}>
                  <strong>{heading ? rowLabel(heading, ticketType) : ticketType.name}</strong>
                </p>
                <p className="mb-0"
                   style={{"fontSize": "16px"}}>
                  {ticketType.free ? (
                    <span>FREE</span>
                  ) : (
                    confirm.enable_all_in_pricing ? (
                      <span>
                        <span>{formatter.format(parseFloat(ticketType.calculated_price))}</span>
                        {confirm.venue_ownership?.show_fee_breakdown_on_ticket_price !== false && (
                          <span className='text-muted ml-2'>{ticketType.all_in_price_description}</span>
                        )}
                      </span>
                    ) : (
                      <span>
                        {formatter.format(parseFloat(ticketType.calculated_price))}
                      </span>
                    )
                  )}
                </p>
                {upcomingPriceText(ticketType, formatter) ? (
                  <p className="mb-0 small text-muted" data-testid={`upcoming-price-${ticketType.id}`}>
                    {upcomingPriceText(ticketType, formatter)}
                  </p>
                ) : null}
                {ticketType.description && ticketType.description.length > 0 ? (
                  <div className="mb-0 mt-2 small last-p-margin-0"
                       dangerouslySetInnerHTML={{__html: ticketType.simple_format_description}}
                       style={{"maxWidth": "400px"}}>
                  </div>
                ) : null}
              </div>
              {ticketType.sales_paused ? (
                <div className="col d-flex align-items-center justify-content-end">
                  <p className="mb-0"
                     style={{"fontSize": "14px", "color": "#686868"}}>
                    Sales paused
                  </p>
                </div>
              ) : ticketType.scheduled ? (
                <div className="col d-flex align-items-center justify-content-end">
                  <p className="mb-0"
                     style={{"fontSize": "14px", "color": "#686868"}}>
                    {ticketType.wait_for_ticket_type && ticketType.wait_for_ticket_type.id ? (
                      <span>Sales not started</span>
                    ) : (
                      <span>Sales start {ticketType.human_sales_start_date}</span>
                    )}
                  </p>
                </div>
              ) : ticketType.sales_ended ? (
                <div className="col d-flex align-items-center justify-content-end">
                  <p className="mb-0"
                     style={{"fontSize": "14px", "color": "#686868"}}>
                    Sale ended
                  </p>
                </div>
              ) : ticketType.sold_out ? (
                <div className="col d-flex align-items-center justify-content-end">
                  <p className="mb-0"
                     style={{"fontSize": "14px", "color": "#686868"}}>
                    Sold out
                  </p>
                </div>
              ) : (
                <div className="col-xs-auto d-flex justify-content-end"
                     style={{"paddingRight": "15px"}}>
                  <div className="form-group mb-0">
                    <select className="form-control form-control-lg"
                            aria-label="Ticket Quantity"
                            onChange={
                              (e) => {
                                var quantity = parseInt(e.target.value);

                                if(quantity > 0){
                                  var updatedItem = Object.assign({}, ticketType, {
                                    quantity: quantity
                                  });

                                  var items = formatItemsForGoogleTagManager(
                                    confirm,
                                    promoCode,
                                    [updatedItem],
                                    []
                                  );

                                  postToParent("ga.add_to_cart", items)
                                  trackXPixelEvent(X_PIXEL_ADD_TO_CART, confirm, {
                                    value: updatedItem.calculated_price * quantity,
                                    numItems: quantity
                                  });
                                  
                                  if (typeof window.rdt !== 'undefined') {
                                    window.rdt('track', 'AddToCart', {
                                      currency: 'USD',
                                      value: updatedItem.calculated_price * quantity,
                                      itemCount: quantity,
                                      products: [{
                                        id: confirm.id.toString(),
                                        name: confirm.title,
                                        category: "Tickets"
                                      }]
                                    });
                                  }
                                }

                                updateTicketTypeQuantity({ ticketTypeId: ticketType.id, quantity });
                                
                                var updated = [...ticketTypes].map((tt) => {
                                  if(tt.id === ticketType.id){
                                    return Object.assign({}, tt, {quantity: quantity});
                                  } else {
                                    return tt;
                                  }
                                });
                                buildTicketReservation(csrfToken, confirm, updated, promoCode, addOns, ticketReservation, userContext);
                              }
                            }
                            value={ticketType.quantity || 0}
                            style={{"fontSize": "16px", "border": "1px solid #f3f3f3"}}>
                      {quantityOptionsWithinOrderLimit(rows, ticketType).map((quantity, index) =>
                        <option key={index} value={quantity}>
                          {quantity}
                        </option>
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}
      {ownsUnavailableState && availibleTickets.length === 0 ? (
        confirm.preselling ? null : salesStartInFuture(confirm) ? (
          announceListEnabled ? (
            <CalendarEventInterestForm
              confirm={confirm}
              csrfToken={csrfToken}
              interestType="Announce"
            />
          ) : (
            <div className="card border-0 h-100" style={{"background": "#f9f9f9"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                    style={{"fontSize": "16px"}}>
                    <strong>Coming soon!</strong>
                  </p>
                  <p className="mb-0">
                    Sales not started yet.
                  </p>
                </div>
              </div>
            </div>
          )
        ) : (
          waitlistEnabled && !anyVisibleTicketTypePaused ? (
            <CalendarEventInterestForm
              confirm={confirm}
              csrfToken={csrfToken}
              interestType="Waitlist"
            />
          ) : (
            <div className="card border-0 h-100"
                  style={{"background": "#f9f9f9"}}>
              <div className="card-body d-flex align-items-center justify-content-center">
                <div className="text-center">
                  <p className="mb-2"
                      style={{"fontSize": "16px"}}>
                    <strong>Tickets not currently available</strong>
                  </p>
                  {anyVisibleTicketTypePaused ? (
                    <p className="mb-0">
                      Online sales for this event are paused.
                    </p>
                  ) : scheduledTickets.length > 0 ? (
                    <>
                      <p className="mb-0">
                        Online sales for this event are currently not available.
                      </p>
                      <p className="mb-0">
                        {`The next sale will begin on ${scheduledTickets[0].human_start_time}.`}
                      </p>
                    </>
                  ) : (
                    <p className="mb-0">
                      Online sales for this event have ended.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        )
      ): null}
    </div>
  )
};

ChooseTicketTypes.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  updateTicketTypeQuantity: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  visibleTicketTypes: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  ticketReservation: PropTypes.object,
  postToParent: PropTypes.func.isRequired,
  formatItemsForGoogleTagManager: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  ticketGroups: PropTypes.object.isRequired,
  findTicketGroup: PropTypes.func.isRequired,
  ownsUnavailableState: PropTypes.bool,
  previewFilter: PropTypes.func
};

export default ChooseTicketTypes;
